import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import PageContainer from '../components/PageContainer';
import { ListCardField } from '../components/ListCardField';
import { useIsXs } from '../hooks/useMobileLayout';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  listSindicatos, createSindicato, updateSindicato, deleteSindicato, listTenants,
} from '../api/services';
import type { Sindicato } from '../../types/domain';
import { useAppDispatch, useAppSelector } from '../store/store';
import { showMessage } from '../store/uiSlice';

interface FormState {
  sindicatoId?: number;
  code: string;
  name: string;
  description: string;
  tenantId: number | null;
  active: boolean;
}

const blank = (): FormState => ({
  code: '', name: '', description: '', tenantId: null, active: true,
});

function uniqueOrgOptions(camaras: Sindicato[]) {
  const m = new Map<number, string>();
  camaras.forEach((c) => {
    const label = c.tenantName || `Organización #${c.tenantId}`;
    m.set(c.tenantId, label);
  });
  return [...m.entries()].map(([tenantId, name]) => ({ tenantId, name }));
}

export default function AdminSindicatos() {
  const isXs = useIsXs();
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const isSuperAdmin = !!user?.isSuperAdmin;

  const [allRows, setAllRows] = useState<Sindicato[]>([]);
  const [filterSindicatoId, setFilterSindicatoId] = useState<number | null>(null);
  const [orgOptions, setOrgOptions] = useState<{ tenantId: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(blank());
  const [busy, setBusy] = useState(false);

  const displayRows = useMemo(() => {
    if (!isSuperAdmin || filterSindicatoId == null) return allRows;
    return allRows.filter((s) => s.sindicatoId === filterSindicatoId);
  }, [allRows, filterSindicatoId, isSuperAdmin]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      if (isSuperAdmin) {
        const res = await listSindicatos({ includeInactive: true });
        setAllRows(res.sindicatos);
        let orgs = uniqueOrgOptions(res.sindicatos);
        if (!orgs.length) {
          try {
            const tRes = await listTenants({ includeInactive: false });
            orgs = tRes.tenants.map((t) => ({ tenantId: t.tenantId, name: t.name }));
          } catch {
            /* ignore */
          }
        }
        setOrgOptions(orgs);
      } else {
        const tid = user?.tenantId;
        if (tid == null) {
          setAllRows([]);
        } else {
          const res = await listSindicatos({ tenantId: tid, includeInactive: true });
          setAllRows(res.sindicatos);
        }
        setOrgOptions([]);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Error al cargar CAMARAS';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isSuperAdmin, user?.tenantId]);

  const canCreate = !isSuperAdmin || orgOptions.length > 0;

  function startCreate() {
    setForm({
      ...blank(),
      tenantId: isSuperAdmin ? (orgOptions[0]?.tenantId ?? null) : (user?.tenantId ?? null),
    });
    setOpen(true);
  }

  function startEdit(s: Sindicato) {
    setForm({
      sindicatoId: s.sindicatoId,
      code: s.code,
      name: s.name,
      description: s.description || '',
      tenantId: s.tenantId,
      active: s.active,
    });
    setOpen(true);
  }

  async function handleSave() {
    if (!form.tenantId) {
      dispatch(showMessage({ message: 'Indicá la organización de CAMARAS', severity: 'error' }));
      return;
    }
    setBusy(true);
    try {
      if (form.sindicatoId) {
        await updateSindicato(form.sindicatoId, {
          name: form.name,
          code: form.code,
          description: form.description || undefined,
          active: form.active,
        });
        dispatch(showMessage({ message: 'Registro CAMARAS actualizado', severity: 'success' }));
      } else {
        await createSindicato({
          tenantId: isSuperAdmin ? form.tenantId : undefined,
          code: form.code.trim(),
          name: form.name.trim(),
          description: form.description || undefined,
          active: form.active,
        });
        dispatch(showMessage({ message: 'Registro CAMARAS creado', severity: 'success' }));
      }
      setOpen(false);
      load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'No se pudo guardar';
      dispatch(showMessage({ message: msg, severity: 'error' }));
    } finally {
      setBusy(false);
    }
  }

  async function handleDeactivate(s: Sindicato) {
    if (!window.confirm(`¿Desactivar "${s.name}"? Las tarifas vinculadas se pueden seguir viendo; no se borran datos de pagos.`)) return;
    try {
      await deleteSindicato(s.sindicatoId);
      dispatch(showMessage({ message: 'Registro CAMARAS desactivado', severity: 'success' }));
      load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Error';
      dispatch(showMessage({ message: msg, severity: 'error' }));
    }
  }

  return (
    <PageContainer>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>CAMARAS</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Unidades operativas. Las tarifas y usuarios se asocian a la organización de cada registro CAMARAS.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: 1, sm: 'auto' } }}>
          {isSuperAdmin && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen(true)}
              fullWidth={isXs}
              sx={{ fontWeight: 700, textTransform: 'none' }}
            >
              Filtros
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={startCreate}
            disabled={!canCreate}
            fullWidth={isXs}
          >
            Agregar CAMARAS
          </Button>
        </Stack>
      </Stack>

      <Dialog open={filtersOpen} onClose={() => setFiltersOpen(false)} maxWidth="sm" fullWidth fullScreen={isXs} scroll="body">
        <DialogTitle sx={{ fontWeight: 800 }}>Filtros</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>CAMARAS</InputLabel>
              <Select
                label="CAMARAS"
                value={filterSindicatoId ?? ''}
                onChange={(e) => setFilterSindicatoId(e.target.value === '' ? null : Number(e.target.value))}
              >
                <MenuItem value="">Todas</MenuItem>
                {allRows.map((s) => (
                  <MenuItem key={s.sindicatoId} value={s.sindicatoId}>
                    {s.name}{s.tenantName ? ` — ${s.tenantName}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => setFiltersOpen(false)} fullWidth={isXs}>Listo</Button>
        </DialogActions>
      </Dialog>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ overflow: 'hidden', p: { xs: 1.5, sm: 2 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : displayRows.length ? (
          <Grid container spacing={2}>
            {displayRows.map((s) => (
              <Grid key={s.sindicatoId} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                          <Chip size="small" label={s.code} variant="outlined" />
                          <Chip size="small" color={s.active ? 'success' : 'default'} label={s.active ? 'Activo' : 'Inactivo'} />
                        </Stack>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{s.name}</Typography>
                      </Box>
                      <Stack direction="row" spacing={0}>
                        <Tooltip title="Editar"><span><IconButton onClick={() => startEdit(s)} size="small"><EditIcon fontSize="small" /></IconButton></span></Tooltip>
                        <Tooltip title="Desactivar"><span><IconButton onClick={() => handleDeactivate(s)} size="small" color="error"><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
                      </Stack>
                    </Stack>
                    <ListCardField label="Organización">
                      <Typography variant="body2">{s.tenantName || `·${s.tenantId}`}</Typography>
                    </ListCardField>
                    <ListCardField label="Descripción">
                      <Typography variant="body2" color="text.secondary">{s.description || '—'}</Typography>
                    </ListCardField>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No hay registros CAMARAS cargados.
          </Typography>
        )}
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth fullScreen={isXs} scroll="paper">
        <DialogTitle>{form.sindicatoId ? 'Editar registro CAMARAS' : 'Nuevo registro CAMARAS'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {isSuperAdmin && !form.sindicatoId && (
              <FormControl size="small" fullWidth required>
                <InputLabel>Organización</InputLabel>
                <Select
                  label="Organización"
                  value={form.tenantId ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, tenantId: e.target.value === '' ? null : Number(e.target.value) }))}
                >
                  {orgOptions.map((o) => (
                    <MenuItem key={o.tenantId} value={o.tenantId}>{o.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              size="small"
              label="Código"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              required
              disabled={!!form.sindicatoId}
              fullWidth
              helperText="Único por organización, sin espacios recomendado"
            />
            <TextField size="small" label="Nombre" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required fullWidth />
            <TextField size="small" label="Descripción" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} fullWidth multiline rows={2} />
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                label={form.active ? 'Activo' : 'Inactivo'}
                color={form.active ? 'success' : 'default'}
                onClick={() => setForm((f) => ({ ...f, active: !f.active }))}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1 }}>
          <Button onClick={() => setOpen(false)} fullWidth={isXs}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={busy} fullWidth={isXs}>
            {busy ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
